import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { PartnerAnalyticsService } from '@services/partner-analytic.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { getCurrencySymbol } from '@utils/utils';
import * as moment from 'moment';
import { GridReadyEvent } from 'ag-grid-community';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-partner-report-campaigns',
  templateUrl: './partner-report-campaigns.component.html',
  styleUrls: ['./partner-report-campaigns.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class PartnerReportCampaignsComponent implements OnInit {
  @ViewChild(AtlasGridComponent) grid: AtlasGridComponent;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  tableColumnDefs = null;
  tableGridOptions = null;
  gridAnimationState = 'hide';

  constructor(
    private partnerAnalyticsService: PartnerAnalyticsService,
    private translateService: TranslateService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    const that = this;
    this.createTableReportCampaigns();
    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationState = 'show';
      },
      disablePagination: true,
      rowHeight: 115,
      sortable: true,
      loadFunction: this.reloadList.bind(that),
      filters: this.getFilters()
    });
  }

  reloadList() {
    this.loaderService.show();
    return this.partnerAnalyticsService
      .getReportCampaigns(this.getFilterMonth(), this.getFilterYear())
      .pipe(finalize(() => this.loaderService.hide()));
  }

  private getFilterMonth(): string {
    const onlyMonth = this.grid.filtersModel.months;
    if (onlyMonth) {
      // const month = moment(onlyMonth).month() + 1;
      return onlyMonth.value.toString();
    }
    return null;
  }

  private getFilterYear(): string {
    const onlyYear = this.grid.filtersModel.years;
    if (onlyYear) {
      const year = moment(onlyYear).year();
      return year.toString();
    }
    return null;
  }

  getFilters() {
    return [
      {
        name: 'years',
        label: 'common_year',
        type: 'dateShowOnlyYears'
      },
      {
        name: 'months',
        label: 'common_month',
        type: 'dropdown',
        options: [
          {
            label: 'month_1',
            value: 1,
            translate: true
          },
          {
            label: 'month_2',
            value: 2,
            translate: true
          },
          {
            label: 'month_3',
            value: 3,
            translate: true
          },
          {
            label: 'month_4',
            value: 4,
            translate: true
          },
          {
            label: 'month_5',
            value: 5,
            translate: true
          },
          {
            label: 'month_6',
            value: 6,
            translate: true
          },
          {
            label: 'month_7',
            value: 7,
            translate: true
          },
          {
            label: 'month_8',
            value: 8,
            translate: true
          },
          {
            label: 'month_9',
            value: 9,
            translate: true
          },
          {
            label: 'month_10',
            value: 10,
            translate: true
          },
          {
            label: 'month_11',
            value: 11,
            translate: true
          },
          {
            label: 'month_12',
            value: 12,
            translate: true
          }
        ]
      }
    ];
  }

  private getTranslateTypeCampaign(value: string) {
    if (value) {
      let labels;
      const itemsLabel = 'partner_campaigntype_' + value.toLowerCase();
      this.translateService.get(itemsLabel).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private verifyTotalItems(type: string, value: string) {
    if (value) {
      if (type && type === 'T') {
        value = '<b>' + value;
      }
      return value;
    }
  }

  private getProfit(value: string, type: string) {
    const profitValue = value + ' ' + getCurrencySymbol();
    return this.verifyTotalItems(type, profitValue);
  }

  private createTableReportCampaigns() {
    this.tableColumnDefs = [
      {
        headerNameTranslate: 'partner_analytics_campaignstable_campaign',
        field: 'typeCampaign',
        cellRenderer: type => {
          const value = this.getTranslateTypeCampaign(type.data?.typeCampaign);
          return this.verifyTotalItems(type.data?.typeCampaign, value);
        }
      },
      {
        headerNameTranslate: 'partner_analytics_campaignstable_visitors',
        field: 'visitors',
        cellRenderer: type => {
          const value = type.data?.visitors;
          return this.verifyTotalItems(type.data?.typeCampaign, value);
        }
      },
      {
        headerNameTranslate: 'partner_analytics_campaignstable_registered',
        field: 'registered',
        cellRenderer: type => {
          const value = type.data?.registered;
          return this.verifyTotalItems(type.data?.typeCampaign, value);
        }
      },
      {
        headerNameTranslate: 'partner_analytics_campaignstable_born_customers',
        field: 'newCustomers',
        cellRenderer: type => {
          const value = type.data?.newCustomers;
          return this.verifyTotalItems(type.data?.typeCampaign, value);
        }
      },
      {
        headerNameTranslate: 'partner_analytics_campaignstable_born_orders',
        field: 'newPurchases',
        cellRenderer: type => {
          const value = type.data?.newPurchases;
          return this.verifyTotalItems(type.data?.typeCampaign, value);
        }
      },
      {
        headerNameTranslate: 'partner_analytics_campaignstable_orders',
        field: 'totalPurchases',
        cellRenderer: type => {
          const value = type.data?.totalPurchases;
          return this.verifyTotalItems(type.data?.typeCampaign, value);
        }
      },
      {
        headerNameTranslate: 'partner_analytics_campaignstable_born_earnings',
        field: 'newProfit',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? this.getProfit(params.data.newProfit, params.data.typeCampaign) : '')
        }
      },
      {
        headerNameTranslate: 'partner_analytics_campaignstable_earnings',
        field: 'totalProfit',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? this.getProfit(params.data.totalProfit, params.data.typeCampaign) : '')
        }
      }
    ];
  }
}
